// Forward Deployed Engineering page, all copy, three locales, written natively.
// Icons and diagram geometry stay in the component; every word lives here.

const fdeContent = {
  'en-US': {
    seo: {
      title: 'Forward Deployed Engineering | Lean it 101',
      description:
        'Senior builders embedded in your most important workflows. We turn AI opportunities, legacy systems, and operational bottlenecks into reliable software running in production.',
    },
    hero: {
      kicker: 'Forward Deployed Engineering · by Lean it 101',
      h1lead: 'Senior builders embedded in your',
      h1hot: 'most important workflows.',
      body: 'We turn AI opportunities, legacy systems, and operational bottlenecks into reliable software running in production.',
      ctaPrimary: 'Get started',
      ctaSecondary: 'How it works',
    },
    heroSvg: {
      aria: 'From operational complexity, through forward deployed engineers who align, build, integrate and iterate, to reliable software in production.',
      leftTitle1: 'OPERATIONAL', leftTitle2: 'COMPLEXITY',
      leftItems: ['Support tickets', 'Billing & invoicing', 'Onboarding', 'Data & reporting', 'Manual handoffs'],
      centerTitle1: 'FORWARD DEPLOYED', centerTitle2: 'ENGINEERS',
      stages: ['Align', 'Build', 'Integrate', 'Iterate'],
      rightItems: ['Applications', 'APIs & services', 'Data platforms', 'Monitoring'],
      before: 'BEFORE', embedded: 'EMBEDDED · ALIGNED · ACCOUNTABLE', inProduction: 'IN PRODUCTION',
    },
    pilot: {
      hLead: 'The pilot was', hHot: 'the easy part.',
      body: 'The demo worked. Then came the real environment, where promising initiatives usually slow down.',
      items: [
        ['Fragmented data', 'Spread across systems that never agreed on a shape.'],
        ['Legacy systems', 'Load-bearing, undocumented, unforgiving.'],
        ['Security & governance', 'Access controls, policies, compliance, audit trails.'],
        ['Unpredictable edge cases', 'The 10% the demo never had to survive.'],
        ['Overloaded engineering teams', 'No capacity to absorb one more initiative.'],
        ['Undocumented workflows', 'How the work happens lives in people’s heads.'],
      ],
      svgAria: 'A working pilot, then a web of real-environment complexity.',
      pilotLabel: 'THE PILOT', pilotSub: 'promising results',
      nodes: ['Fragmented data', 'Legacy systems', 'Governance', 'Edge cases', 'Overloaded teams', 'Hidden workflows'],
    },
    what: {
      hLead: 'What Forward Deployed', hHot: 'Engineering means.',
      blocks: [
        ['Embedded with your teams', 'Our engineers sit inside your business and technical teams and work alongside them every day.'],
        ['Decisions close to the problem', 'They learn how the operation actually works and write production code inside your environment.'],
        ['Accountable end to end', 'They take responsibility from discovery through deployment, adoption, and improvement.'],
      ],
      rail: [
        ['Discover', 'Clarify problems & outcomes'],
        ['Architect', 'Design the right approach'],
        ['Code', 'Build in your environment'],
        ['Ship', 'Deploy, adopt, iterate'],
      ],
    },
    code: {
      hLead: 'From code', hHot: 'to customer.',
      body: 'Our forward deployed engineers combine six disciplines end to end.',
      codeLabel: 'Code', customerLabel: 'Customer',
      combine: ['Product thinking', 'Software engineering', 'AI & data architecture', 'Workflow redesign', 'Systems integration', 'Operational problem-solving'],
      line1a: 'From code to customer,', line1hot: 'from concept to cash.',
      line2: 'The goal is working software that improves how the business operates.',
    },
    value: {
      hLead: 'Where we', hHot: 'create value.',
      body: 'Workflows where complexity, manual effort, and technical constraints meet.',
      items: ['AI agents & support automation', 'Internal workflow systems', 'Legacy platform modernization', 'AI features for existing products', 'Complex system & API integrations', 'Knowledge & data pipelines', 'Evaluation, monitoring & oversight', 'Rescuing stalled prototypes'],
    },
    benjamin: {
      hLead: 'Platform experience', hHot: 'accelerates delivery.',
      p1a: 'Every pod works inside ', p1name: 'Maestro', p1b: ', the operating system we built for software delivery in the AI era: humans and AI agents on the same board, under the same written rules.',
      p2: 'Priorities, forecasts and every action visible to you from day one. Nothing happens behind a curtain, and the record stays with you when the engagement ends.',
      svgAria: 'Maestro, the operating system for software delivery, and its capabilities.',
      items: ['Live delivery board', 'Flow policies that execute', 'Monte Carlo forecasts', 'Agent attribution', 'Full audit trail', 'Release maps', 'Real-time metrics', 'Versioned standards'],
      core2: 'OPERATING SYSTEM', core3: 'Visible · Governed',
    },
    engagement: {
      hLead: 'How an', hHot: 'engagement works.',
      steps: [
        ['Understand the workflow', 'Work with the people closest to the operation, map the current reality, find the first valuable outcome.'],
        ['Ship the first system', 'A senior embedded pod designs, builds, integrates, and deploys it into production.'],
        ['Measure it in production', 'We track adoption, reliability, cycle time, quality, cost, and operational impact.'],
        ['Build internal capability', 'Runbooks, architecture decisions, monitoring, governance, and a team ready to operate it.'],
      ],
    },
    ownership: {
      hLead: 'Senior people. Small team.', hHot: 'Direct ownership.',
      points: [
        'Experienced engineers who move between business problems, architecture, code, infrastructure, and production operations.',
        'No long chain of account managers.',
        'No handoff from senior consultants to junior delivery teams.',
        'The people understanding the problem are the people building the solution.',
      ],
      leanTitle: 'Lean, embedded',
      leanChips: ['Align', 'Build', 'Deploy', 'Operate'],
      leanFoot1: 'Direct ownership.', leanFoot2: 'End-to-end impact.',
      tradTitle: 'Traditional stack',
      tradRows: ['Account Management', 'Solution Architects', 'Project Managers', 'Business Analysts', 'Developers', 'QA / Testers', 'Operations'],
      tradFoot1: 'Handoffs. Delays.', tradFoot2: 'Diluted accountability.',
    },
    proof: {
      hLead: 'Built on decades', hHot: 'of real delivery.',
      items: [
        ['Vale', 'R$50M+', 'in annual savings', 'Operational redesign across process, leadership, and systems.'],
        ['Softplan', '80%+', 'support tickets reduction', 'Improved the flow of software delivery; restored SLA compliance at scale.'],
        ['Segware · Sigma Cloud', 'Legacy → scale', 'platform rebuild', 'Rebuilt a legacy SaaS product into a modern platform prepared for scale.'],
        ['Benjamin', 'Ground-up', 'AI platform', 'Support, qualification, knowledge retrieval, workflow automation, enterprise integrations.'],
      ],
    },
    cta: {
      hLead: 'Bring us one', hHot: 'painful workflow.',
      body: 'Something slow, repetitive, expensive, unreliable, or stuck between the business and engineering teams. We’ll find the shortest responsible path to production.',
      pains: ['Slow', 'Unreliable', 'Expensive', 'Repetitive', 'Stuck between teams'],
      footerTag: 'The shortest responsible path to production',
      orEmail: 'Or email',
    },
    form: {
      name: 'Name', email: 'Work email', company: 'Company', optional: '(optional)', message: 'The workflow',
      namePh: 'Your name', emailPh: 'you@company.com', companyPh: 'Company',
      messagePh: 'The slow, repetitive, expensive, or stuck workflow you’d bring us.',
      submit: 'Send it over', sending: 'Sending…',
      successTitle: 'Got it. Talk soon.',
      successBody: 'We reply within one business day to line up the first conversation.',
      errName: 'Please enter your name.', errEmail: 'Please enter a valid work email.',
      errMessage: 'A sentence about the workflow helps.',
      errCheck: 'Please check the fields and try again.',
      errSend: 'Something went wrong sending this. Try again, or email contact@leanit101.com.',
      footNote: 'We reply within one business day. No sales sequence.',
    },
  },

  'pt-BR': {
    seo: {
      title: 'Forward Deployed Engineering | Lean it 101',
      description:
        'Builders seniores embarcados nos seus workflows mais importantes. Transformamos oportunidades de IA, sistemas legados e gargalos operacionais em software confiável rodando em produção.',
    },
    hero: {
      kicker: 'Forward Deployed Engineering · por Lean it 101',
      h1lead: 'Builders seniores embarcados nos seus',
      h1hot: 'workflows mais importantes.',
      body: 'Transformamos oportunidades de IA, sistemas legados e gargalos operacionais em software confiável rodando em produção.',
      ctaPrimary: 'Começar agora',
      ctaSecondary: 'Como funciona',
    },
    heroSvg: {
      aria: 'Da complexidade operacional, passando por engenheiros forward deployed que alinham, constroem, integram e iteram, até software confiável em produção.',
      leftTitle1: 'COMPLEXIDADE', leftTitle2: 'OPERACIONAL',
      leftItems: ['Tickets de suporte', 'Cobrança e faturamento', 'Onboarding', 'Dados e relatórios', 'Handoffs manuais'],
      centerTitle1: 'ENGENHEIROS', centerTitle2: 'FORWARD DEPLOYED',
      stages: ['Alinhar', 'Construir', 'Integrar', 'Iterar'],
      rightItems: ['Aplicações', 'APIs e serviços', 'Plataformas de dados', 'Monitoramento'],
      before: 'ANTES', embedded: 'EMBARCADOS · ALINHADOS · RESPONSÁVEIS', inProduction: 'EM PRODUÇÃO',
    },
    pilot: {
      hLead: 'O piloto era', hHot: 'a parte fácil.',
      body: 'A demo funcionou. Depois veio o ambiente real, onde iniciativas promissoras costumam desacelerar.',
      items: [
        ['Dados fragmentados', 'Espalhados por sistemas que nunca combinaram um formato.'],
        ['Sistemas legados', 'Estruturais, sem documentação, implacáveis.'],
        ['Segurança e governança', 'Controles de acesso, políticas, compliance, trilhas de auditoria.'],
        ['Casos extremos imprevisíveis', 'Os 10% que a demo nunca precisou sobreviver.'],
        ['Times de engenharia sobrecarregados', 'Sem capacidade para absorver mais uma iniciativa.'],
        ['Workflows sem documentação', 'Como o trabalho acontece vive na cabeça das pessoas.'],
      ],
      svgAria: 'Um piloto funcionando, e então uma teia de complexidade do ambiente real.',
      pilotLabel: 'O PILOTO', pilotSub: 'resultados promissores',
      nodes: ['Dados fragmentados', 'Sistemas legados', 'Governança', 'Casos extremos', 'Times sobrecarregados', 'Workflows ocultos'],
    },
    what: {
      hLead: 'O que Forward Deployed', hHot: 'Engineering significa.',
      blocks: [
        ['Embarcados nos seus times', 'Nossos engenheiros sentam dentro dos seus times de negócio e de tecnologia e trabalham lado a lado todos os dias.'],
        ['Decisões perto do problema', 'Eles aprendem como a operação funciona de verdade e escrevem código de produção dentro do seu ambiente.'],
        ['Responsáveis de ponta a ponta', 'Assumem a responsabilidade do discovery à implantação, adoção e melhoria.'],
      ],
      rail: [
        ['Descobrir', 'Clarear problemas e resultados'],
        ['Arquitetar', 'Desenhar a abordagem certa'],
        ['Codar', 'Construir no seu ambiente'],
        ['Entregar', 'Implantar, adotar, iterar'],
      ],
    },
    code: {
      hLead: 'Do código', hHot: 'ao cliente.',
      body: 'Nossos engenheiros forward deployed combinam seis disciplinas de ponta a ponta.',
      codeLabel: 'Código', customerLabel: 'Cliente',
      combine: ['Pensamento de produto', 'Engenharia de software', 'Arquitetura de IA e dados', 'Redesenho de workflows', 'Integração de sistemas', 'Resolução de problemas operacionais'],
      line1a: 'Do código ao cliente,', line1hot: 'do conceito ao caixa.',
      line2: 'O objetivo é software funcionando que melhora como o negócio opera.',
    },
    value: {
      hLead: 'Onde criamos', hHot: 'valor.',
      body: 'Workflows onde complexidade, esforço manual e restrições técnicas se encontram.',
      items: ['Agentes de IA e automação de suporte', 'Sistemas de workflow internos', 'Modernização de plataformas legadas', 'Features de IA para produtos existentes', 'Integrações complexas de sistemas e APIs', 'Pipelines de conhecimento e dados', 'Avaliação, monitoramento e supervisão', 'Resgate de protótipos parados'],
    },
    benjamin: {
      hLead: 'Experiência de plataforma', hHot: 'acelera a entrega.',
      p1a: 'Todo pod trabalha dentro do ', p1name: 'Maestro', p1b: ', o sistema operacional que construímos para a entrega de software na era da IA: humanos e agentes de IA no mesmo board, sob as mesmas regras escritas.',
      p2: 'Prioridades, previsões e cada ação visíveis para você desde o primeiro dia. Nada acontece atrás de cortina, e o registro fica com você quando o engajamento termina.',
      svgAria: 'Maestro, o sistema operacional da entrega de software, e suas capacidades.',
      items: ['Board de entrega ao vivo', 'Políticas de fluxo que executam', 'Previsões Monte Carlo', 'Atribuição de agentes', 'Trilha de auditoria completa', 'Mapas de release', 'Métricas em tempo real', 'Padrões versionados'],
      core2: 'SISTEMA OPERACIONAL', core3: 'Visível · Governado',
    },
    engagement: {
      hLead: 'Como um', hHot: 'engajamento funciona.',
      steps: [
        ['Entender o workflow', 'Trabalhar com as pessoas mais próximas da operação, mapear a realidade atual, encontrar o primeiro resultado valioso.'],
        ['Entregar o primeiro sistema', 'Um pod sênior embarcado desenha, constrói, integra e implanta em produção.'],
        ['Medir em produção', 'Acompanhamos adoção, confiabilidade, cycle time, qualidade, custo e impacto operacional.'],
        ['Construir capacidade interna', 'Runbooks, decisões de arquitetura, monitoramento, governança e um time pronto para operar.'],
      ],
    },
    ownership: {
      hLead: 'Gente sênior. Time pequeno.', hHot: 'Dono direto.',
      points: [
        'Engenheiros experientes que transitam entre problemas de negócio, arquitetura, código, infraestrutura e operação em produção.',
        'Sem corrente comprida de account managers.',
        'Sem handoff de consultores seniores para times de entrega juniores.',
        'Quem entende o problema é quem constrói a solução.',
      ],
      leanTitle: 'Enxuto, embarcado',
      leanChips: ['Alinhar', 'Construir', 'Implantar', 'Operar'],
      leanFoot1: 'Dono direto.', leanFoot2: 'Impacto de ponta a ponta.',
      tradTitle: 'Estrutura tradicional',
      tradRows: ['Account Management', 'Arquitetos de Solução', 'Gerentes de Projeto', 'Analistas de Negócio', 'Desenvolvedores', 'QA / Testers', 'Operações'],
      tradFoot1: 'Handoffs. Atrasos.', tradFoot2: 'Responsabilidade diluída.',
    },
    proof: {
      hLead: 'Construído sobre décadas', hHot: 'de entrega real.',
      items: [
        ['Vale', 'R$50M+', 'em economia anual', 'Redesenho operacional através de processo, liderança e sistemas.'],
        ['Softplan', '80%+', 'de redução em tickets de suporte', 'Melhorou o fluxo de entrega de software; SLA restaurado em escala.'],
        ['Segware · Sigma Cloud', 'Legado → escala', 'reconstrução de plataforma', 'Produto SaaS legado reconstruído em uma plataforma moderna preparada para escala.'],
        ['Benjamin', 'Do zero', 'plataforma de IA', 'Suporte, qualificação, recuperação de conhecimento, automação de workflows, integrações enterprise.'],
      ],
    },
    cta: {
      hLead: 'Traga um workflow', hHot: 'que dói.',
      body: 'Algo lento, repetitivo, caro, não confiável, ou preso entre o negócio e a engenharia. Encontramos o caminho responsável mais curto até produção.',
      pains: ['Lento', 'Não confiável', 'Caro', 'Repetitivo', 'Preso entre times'],
      footerTag: 'O caminho responsável mais curto até produção',
      orEmail: 'Ou escreva para',
    },
    form: {
      name: 'Nome', email: 'Email corporativo', company: 'Empresa', optional: '(opcional)', message: 'O workflow',
      namePh: 'Seu nome', emailPh: 'voce@empresa.com.br', companyPh: 'Empresa',
      messagePh: 'O workflow lento, repetitivo, caro ou travado que você traria para a gente.',
      submit: 'Enviar', sending: 'Enviando…',
      successTitle: 'Recebido. Falamos em breve.',
      successBody: 'Respondemos em até um dia útil para marcar a primeira conversa.',
      errName: 'Informe o seu nome.', errEmail: 'Informe um email corporativo válido.',
      errMessage: 'Uma frase sobre o workflow ajuda.',
      errCheck: 'Confira os campos e tente de novo.',
      errSend: 'Algo deu errado no envio. Tente de novo, ou escreva para contact@leanit101.com.',
      footNote: 'Respondemos em até um dia útil. Sem sequência de vendas.',
    },
  },

  es: {
    seo: {
      title: 'Forward Deployed Engineering | Lean it 101',
      description:
        'Builders seniores integrados en tus workflows más importantes. Convertimos oportunidades de IA, sistemas heredados y cuellos de botella operativos en software confiable corriendo en producción.',
    },
    hero: {
      kicker: 'Forward Deployed Engineering · por Lean it 101',
      h1lead: 'Builders seniores integrados en tus',
      h1hot: 'workflows más importantes.',
      body: 'Convertimos oportunidades de IA, sistemas heredados y cuellos de botella operativos en software confiable corriendo en producción.',
      ctaPrimary: 'Empezar ahora',
      ctaSecondary: 'Cómo funciona',
    },
    heroSvg: {
      aria: 'De la complejidad operativa, pasando por ingenieros forward deployed que alinean, construyen, integran e iteran, hasta software confiable en producción.',
      leftTitle1: 'COMPLEJIDAD', leftTitle2: 'OPERATIVA',
      leftItems: ['Tickets de soporte', 'Cobros y facturación', 'Onboarding', 'Datos y reportes', 'Handoffs manuales'],
      centerTitle1: 'INGENIEROS', centerTitle2: 'FORWARD DEPLOYED',
      stages: ['Alinear', 'Construir', 'Integrar', 'Iterar'],
      rightItems: ['Aplicaciones', 'APIs y servicios', 'Plataformas de datos', 'Monitoreo'],
      before: 'ANTES', embedded: 'INTEGRADOS · ALINEADOS · RESPONSABLES', inProduction: 'EN PRODUCCIÓN',
    },
    pilot: {
      hLead: 'El piloto era', hHot: 'la parte fácil.',
      body: 'La demo funcionó. Después llegó el entorno real, donde las iniciativas prometedoras suelen frenarse.',
      items: [
        ['Datos fragmentados', 'Repartidos en sistemas que nunca acordaron un formato.'],
        ['Sistemas heredados', 'Estructurales, sin documentación, implacables.'],
        ['Seguridad y gobernanza', 'Controles de acceso, políticas, compliance, trazas de auditoría.'],
        ['Casos extremos impredecibles', 'El 10% que la demo nunca tuvo que sobrevivir.'],
        ['Equipos de ingeniería sobrecargados', 'Sin capacidad para absorber una iniciativa más.'],
        ['Workflows sin documentar', 'Cómo se hace el trabajo vive en la cabeza de la gente.'],
      ],
      svgAria: 'Un piloto funcionando, y luego una red de complejidad del entorno real.',
      pilotLabel: 'EL PILOTO', pilotSub: 'resultados prometedores',
      nodes: ['Datos fragmentados', 'Sistemas heredados', 'Gobernanza', 'Casos extremos', 'Equipos sobrecargados', 'Workflows ocultos'],
    },
    what: {
      hLead: 'Qué significa Forward', hHot: 'Deployed Engineering.',
      blocks: [
        ['Integrados en tus equipos', 'Nuestros ingenieros se sientan dentro de tus equipos de negocio y tecnología y trabajan a su lado todos los días.'],
        ['Decisiones cerca del problema', 'Aprenden cómo opera el negocio de verdad y escriben código de producción dentro de tu entorno.'],
        ['Responsables de punta a punta', 'Asumen la responsabilidad del discovery a la implementación, adopción y mejora.'],
      ],
      rail: [
        ['Descubrir', 'Aclarar problemas y resultados'],
        ['Arquitectar', 'Diseñar el enfoque correcto'],
        ['Codificar', 'Construir en tu entorno'],
        ['Entregar', 'Implementar, adoptar, iterar'],
      ],
    },
    code: {
      hLead: 'Del código', hHot: 'al cliente.',
      body: 'Nuestros ingenieros forward deployed combinan seis disciplinas de punta a punta.',
      codeLabel: 'Código', customerLabel: 'Cliente',
      combine: ['Pensamiento de producto', 'Ingeniería de software', 'Arquitectura de IA y datos', 'Rediseño de workflows', 'Integración de sistemas', 'Resolución de problemas operativos'],
      line1a: 'Del código al cliente,', line1hot: 'del concepto a la caja.',
      line2: 'El objetivo es software funcionando que mejora cómo opera el negocio.',
    },
    value: {
      hLead: 'Dónde creamos', hHot: 'valor.',
      body: 'Workflows donde la complejidad, el esfuerzo manual y las restricciones técnicas se encuentran.',
      items: ['Agentes de IA y automatización de soporte', 'Sistemas de workflow internos', 'Modernización de plataformas heredadas', 'Features de IA para productos existentes', 'Integraciones complejas de sistemas y APIs', 'Pipelines de conocimiento y datos', 'Evaluación, monitoreo y supervisión', 'Rescate de prototipos detenidos'],
    },
    benjamin: {
      hLead: 'La experiencia de plataforma', hHot: 'acelera la entrega.',
      p1a: 'Cada pod trabaja dentro de ', p1name: 'Maestro', p1b: ', el sistema operativo que construimos para la entrega de software en la era de la IA: humanos y agentes de IA en el mismo tablero, bajo las mismas reglas escritas.',
      p2: 'Prioridades, pronósticos y cada acción visibles para ti desde el primer día. Nada pasa detrás de una cortina, y el registro queda contigo cuando termina el compromiso.',
      svgAria: 'Maestro, el sistema operativo de la entrega de software, y sus capacidades.',
      items: ['Board de entrega en vivo', 'Políticas de flujo que ejecutan', 'Pronósticos Monte Carlo', 'Atribución de agentes', 'Registro de auditoría completo', 'Mapas de release', 'Métricas en tiempo real', 'Estándares versionados'],
      core2: 'SISTEMA OPERATIVO', core3: 'Visible · Gobernado',
    },
    engagement: {
      hLead: 'Cómo funciona', hHot: 'un compromiso.',
      steps: [
        ['Entender el workflow', 'Trabajar con la gente más cercana a la operación, mapear la realidad actual, encontrar el primer resultado valioso.'],
        ['Entregar el primer sistema', 'Un pod senior integrado diseña, construye, integra e implementa en producción.'],
        ['Medirlo en producción', 'Seguimos adopción, confiabilidad, cycle time, calidad, costo e impacto operativo.'],
        ['Construir capacidad interna', 'Runbooks, decisiones de arquitectura, monitoreo, gobernanza y un equipo listo para operarlo.'],
      ],
    },
    ownership: {
      hLead: 'Gente senior. Equipo pequeño.', hHot: 'Propiedad directa.',
      points: [
        'Ingenieros con experiencia que se mueven entre problemas de negocio, arquitectura, código, infraestructura y operación en producción.',
        'Sin cadena larga de account managers.',
        'Sin handoff de consultores seniores a equipos de entrega juniores.',
        'Quien entiende el problema es quien construye la solución.',
      ],
      leanTitle: 'Ligero, integrado',
      leanChips: ['Alinear', 'Construir', 'Implementar', 'Operar'],
      leanFoot1: 'Propiedad directa.', leanFoot2: 'Impacto de punta a punta.',
      tradTitle: 'Estructura tradicional',
      tradRows: ['Account Management', 'Arquitectos de Solución', 'Gerentes de Proyecto', 'Analistas de Negocio', 'Desarrolladores', 'QA / Testers', 'Operaciones'],
      tradFoot1: 'Handoffs. Demoras.', tradFoot2: 'Responsabilidad diluida.',
    },
    proof: {
      hLead: 'Construido sobre décadas', hHot: 'de entrega real.',
      items: [
        ['Vale', 'R$50M+', 'en ahorro anual', 'Rediseño operativo a través de proceso, liderazgo y sistemas.'],
        ['Softplan', '80%+', 'de reducción en tickets de soporte', 'Mejoró el flujo de entrega de software; SLA restaurado a escala.'],
        ['Segware · Sigma Cloud', 'Heredado → escala', 'reconstrucción de plataforma', 'Producto SaaS heredado reconstruido en una plataforma moderna preparada para escala.'],
        ['Benjamin', 'Desde cero', 'plataforma de IA', 'Soporte, calificación, recuperación de conocimiento, automatización de workflows, integraciones enterprise.'],
      ],
    },
    cta: {
      hLead: 'Tráenos un workflow', hHot: 'que duele.',
      body: 'Algo lento, repetitivo, caro, poco confiable, o atascado entre el negocio y la ingeniería. Encontramos el camino responsable más corto a producción.',
      pains: ['Lento', 'Poco confiable', 'Caro', 'Repetitivo', 'Atascado entre equipos'],
      footerTag: 'El camino responsable más corto a producción',
      orEmail: 'O escribe a',
    },
    form: {
      name: 'Nombre', email: 'Email corporativo', company: 'Empresa', optional: '(opcional)', message: 'El workflow',
      namePh: 'Tu nombre', emailPh: 'tu@empresa.com', companyPh: 'Empresa',
      messagePh: 'El workflow lento, repetitivo, caro o atascado que nos traerías.',
      submit: 'Enviar', sending: 'Enviando…',
      successTitle: 'Recibido. Hablamos pronto.',
      successBody: 'Respondemos dentro de un día hábil para agendar la primera conversación.',
      errName: 'Escribe tu nombre.', errEmail: 'Escribe un email corporativo válido.',
      errMessage: 'Una frase sobre el workflow ayuda.',
      errCheck: 'Revisa los campos e inténtalo de nuevo.',
      errSend: 'Algo salió mal al enviar. Inténtalo de nuevo, o escribe a contact@leanit101.com.',
      footNote: 'Respondemos dentro de un día hábil. Sin secuencia de ventas.',
    },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getFdeContent = (language) => fdeContent[normalize(language)]
export default fdeContent
